import React, {useState, useRef, useEffect} from 'react';
import styles from './OnboardingForm.module.css';
import cx from 'classnames';
import Button from '../../components/Button';
import ProgressBar from '../../components/ProgressBar';
import UserDataStep, { UserDataStepRef } from './UserData';
import CIStep from './CI';
import DataStep from './Data';
import ProductionStep from './Production';
import PreviewStep from './Preview';
import ThankYou from './ThankYou';
import {useOnboardingContext} from '../../contexts/OnboardingContext';
import {toSlug} from '../../helpers/stringHelper';
import {api} from '../../services/ApiService';
import {ApiRoutes} from '../../services/ApiService';
import {showToast} from '../../services/ToastService';
import {AxiosError} from 'axios';
import {fontSvgToPng} from '../../utils/fontSvgToPng';

const FALLBACK_ERROR_MESSAGE = 'Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut.';

function getSubmissionErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const res = (error as AxiosError<{ message?: string }>).response;
        const msg = res?.data?.message;
        if (typeof msg === 'string' && msg.trim()) {
            return msg;
        }
    }
    return FALLBACK_ERROR_MESSAGE;
}

interface OnboardingFormProps {
    className?: string;
}

type Step = 'userdata' | 'ci' | 'data' | 'production' | 'preview' | 'thankYou';

/** Workwear: no package step - flow starts with Branche (data) */
const steps: Step[] = ['data', 'ci', 'production', 'userdata', 'preview'];

const OnboardingForm: React.FC<OnboardingFormProps> = ({className}) => {
    const {formData, loadTypes, setSelectedTypes, types} = useOnboardingContext();
    const [currentStep, setCurrentStep] = useState<Step>('data');
    const [completedSteps, setCompletedSteps] = useState<number[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const userDataStepRef = useRef<UserDataStepRef>(null);
    const stepContentRef = useRef<HTMLDivElement>(null);
    const hasAppliedInitialBrancheRef = useRef(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stepParam = urlParams.get('step');
        const brancheParam = urlParams.get('branche');

        if (brancheParam) {
            loadTypes();
        }

        if (stepParam && ['data', 'ci', 'production', 'userdata', 'preview'].includes(stepParam)) {
            setCurrentStep(stepParam as Step);
            const stepIndex = steps.indexOf(stepParam as Step);
            if (stepIndex > 0) {
                setCompletedSteps(Array.from({ length: stepIndex }, (_, i) => i));
            }
        }
    }, [loadTypes]);

    useEffect(() => {
        if (hasAppliedInitialBrancheRef.current) return;

        const urlParams = new URLSearchParams(window.location.search);
        const brancheParam = urlParams.get('branche');
        if (!brancheParam || types.length === 0) {
            if (types.length > 0) hasAppliedInitialBrancheRef.current = true;
            return;
        }

        const slug = brancheParam.toLowerCase().trim();
        const match = types.find((t) => toSlug(t.name) === slug);
        if (match) {
            setSelectedTypes([match]);
            hasAppliedInitialBrancheRef.current = true;
        } else {
            hasAppliedInitialBrancheRef.current = true;
        }
    }, [types, setSelectedTypes]);

    useEffect(() => {
        if (stepContentRef.current) {
            stepContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentStep]);

    const stepLabels: Record<Step, string> = {
        userdata: 'Firmendaten',
        ci: 'Farben & Logos',
        data: 'Branche',
        production: 'Artikelauswahl',
        preview: 'Vorschau & Abschicken',
        thankYou: 'Vielen Dank'
    };

    const currentStepIndex = steps.indexOf(currentStep);
    const stepLabelsArray = steps.map(step => stepLabels[step]);

    const handleNext = () => {
        if (currentStep === 'data') {
            if (formData.selectedTypes.length === 0) return;
            const customTypeSelected = formData.selectedTypes.some(t => t.name === 'Andere branche');
            if (customTypeSelected && !formData.userData.customTypeName.trim()) return;
        }

        if (currentStep === 'production' && formData.selectedArticles.length === 0) {
            return;
        }

        if (currentStep === 'userdata') {
            const isValid = userDataStepRef.current?.validate();
            if (!isValid) {
                return;
            }
        }

        if (currentStepIndex < steps.length - 1) {
            setCompletedSteps(prev => [...prev, currentStepIndex]);
            setCurrentStep(steps[currentStepIndex + 1]);
        }
    };

    const handlePrevious = () => {
        if (currentStepIndex > 0) {
            setCurrentStep(steps[currentStepIndex - 1]);
        }
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const submissionData: Record<string, unknown> = {
                types: formData.selectedTypes.map(type => ({
                    id: type.id,
                    name: type.name,
                })),
                primaryColor: formData.primaryColor,
                secondaryColor: formData.secondaryColor,
                primaryColorName: formData.primaryColorName || formData.primaryColor,
                secondaryColorName: formData.secondaryColorName || formData.secondaryColor,
                font: formData.font,
                style: formData.style,
                logos: formData.logos
                    .filter(logo => logo.file !== null)
                    .map(logo => ({
                        label: logo.label || logo.file?.name || 'Logo',
                        filename: logo.file?.name || '',
                    })),
                userData: {
                    company: formData.userData.company,
                    firstName: formData.userData.firstName,
                    lastName: formData.userData.lastName,
                    customTypeName: formData.userData.customTypeName,
                    street: formData.userData.street,
                    houseNumber: formData.userData.houseNumber,
                    postalCode: formData.userData.postalCode,
                    city: formData.userData.city,
                    email: formData.userData.email,
                    phoneNumber: formData.userData.phoneNumber,
                    memberCount: formData.userData.memberCount,
                    additionalInformation: formData.userData.additionalInformation,
                },
                articles: formData.selectedArticles.map(article => ({
                    id: article.id,
                    name: article.name,
                    articleNumber: article.articleNumber,
                    price: article.price,
                    url: article.url,
                })),
            };

            formData.logos.forEach((logo, index) => {
                if (logo.file) {
                    (submissionData as Record<string, unknown>)[`logo_${index}`] = logo.file;
                }
            });

            const fontStyle = formData.style === 'curved' ? 'curved' : 'normal';
            if ((formData.style === 'normal' || formData.style === 'curved') && formData.selectedFont && formData.font) {
                try {
                    const pngBlob = await fontSvgToPng({
                        font: formData.selectedFont,
                        previewText: formData.fontPreviewText?.trim() || 'workwear',
                        style: fontStyle,
                    });
                    const fontPngFile = new File(
                        [pngBlob],
                        `schriftzug-${fontStyle}.png`,
                        { type: 'image/png' }
                    );
                    (submissionData as Record<string, unknown>)['font_schriftzug'] = fontPngFile;
                } catch (err) {
                    console.error('Failed to generate font PNG:', err);
                    showToast('Schriftzug-Bild konnte nicht erzeugt werden.', 'error');
                    setIsSubmitting(false);
                    return;
                }
            }

            const response = await api.post(ApiRoutes.ONBOARDING_SUBMIT, undefined, submissionData);

            if (response.data?.data?.success) {
                setCompletedSteps(Array.from({ length: steps.length }, (_, i) => i));
                setCurrentStep('thankYou');
            } else {
                const backendMessage =
                    typeof response.data?.message === 'string' && response.data.message.trim()
                        ? response.data.message
                        : FALLBACK_ERROR_MESSAGE;
                showToast(backendMessage, 'error');
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('Submission error:', error);
            showToast(getSubmissionErrorMessage(error), 'error');
            setIsSubmitting(false);
        }
    };

    const renderStep = () => (
        <>
            <UserDataStep
                ref={userDataStepRef}
                isActive={currentStep === 'userdata'}
            />
            <CIStep isActive={currentStep === 'ci'}/>
            <DataStep isActive={currentStep === 'data'}/>
            <ProductionStep isActive={currentStep === 'production'}/>
            <PreviewStep
                onSubmit={handleSubmit}
                isActive={currentStep === 'preview'}
            />
            <ThankYou isActive={currentStep === 'thankYou'}/>
        </>
    );

    const isThankYouStep = currentStep === 'thankYou';

    return (
        <div ref={stepContentRef} className={cx(styles['onboarding-form-container'], className)}>
            {!isThankYouStep && (
                <ProgressBar
                    steps={stepLabelsArray}
                    currentStep={currentStepIndex}
                    completedSteps={completedSteps}
                />
            )}

            <div className={styles['step-content-wrapper']}>
                {renderStep()}
            </div>

            {!isThankYouStep && (
                <div className={styles['step-actions']}>
                    <div>
                        {currentStepIndex > 0 && (
                            <Button
                                hierarchy="secondary"
                                text="Zurück"
                                fn={handlePrevious}
                            />
                        )}
                    </div>
                    <div>
                        {currentStepIndex < steps.length - 1 ? (
                            <Button
                                hierarchy="ts-sport-brown"
                                text="Weiter"
                                fn={handleNext}
                                disabled={
                                    (currentStep === 'data' && (formData.selectedTypes.length === 0 ||
                                        (formData.selectedTypes.some(t => t.name === 'Andere branche') && !formData.userData.customTypeName.trim()))) ||
                                    (currentStep === 'production' && formData.selectedArticles.length === 0)
                                }
                            />
                        ) : (
                            <Button
                                hierarchy="ts-sport"
                                text={isSubmitting ? 'Wird gesendet...' : 'Abschließen'}
                                fn={handleSubmit}
                                disabled={isSubmitting}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnboardingForm;
